import { useEffect, useMemo, useState } from 'react';
import {
  useGetCertificatesQuery,
  useUpdateCertificateMutation,
} from '../../../store/api/certificatesApi';
import { authErrorMessage } from '../../../store/api/authHelpers';
import { toast } from '../../../reusable/Toast/toastStore';
import Pagination from '../../../reusable/DataTable/Pagination';
import FilterDropdown from '../../../reusable/FilterDropdown/FilterDropdown';
import CertificateArt from './CertificateArt';
import { downloadRenderedCertificate } from './certificateExport';
import CertificatesSkeleton from './CertificatesSkeleton';
import ShareCertificateDialog from './ShareCertificateDialog';
import PayoutReviewModal from './PayoutReviewModal';
import { t } from '../../../i18n/i18n';
import './Certificates.scss';

const FILTER_OPTIONS = [
  'All Certificates',
  'Lifetime Payout',
  'Payout Split',
  'Stage 1',
  'Stage 2',
  'Stage 3',
  'Performance Fees',
  'Qualified Profit',
  'Max Allocation',
  'Qualified',
  'Profit Split',
];

const matchesFilter = (item, filter) => {
  if (!filter || filter === 'All Certificates') return true;

  const type = String(item?.type || '').toLowerCase();
  const title = String(item?.title || '').toLowerCase();
  const planType = String(item?.plan_type || '').toLowerCase();
  const text = `${type} ${title} ${planType}`;

  switch (filter) {
    case 'Lifetime Payout':
      return text.includes('lifetime');

    case 'Payout Split':
      return text.includes('split') || (text.includes('payout') && !text.includes('lifetime'));

    case 'Profit Split':
      return text.includes('profit split') || text.includes('split');

    case 'Stage 1':
      return text.includes('stage 1') || text.includes('stage-1') || text.includes('phase 1');

    case 'Stage 2':
      return text.includes('stage 2') || text.includes('stage-2') || text.includes('phase 2');

    case 'Stage 3':
      return text.includes('stage 3') || text.includes('stage-3') || text.includes('phase 3');

    case 'Performance Fees':
      return text.includes('performance') || text.includes('funded');

    // The API still names the type "Funded Profit"; Alpha's word is Qualified.
    case 'Qualified Profit':
      return text.includes('funded') || text.includes('qualified profit') || text.includes('performance');

    case 'Max Allocation':
      return text.includes('allocation');

    case 'Qualified':
      return text.includes('qualified');

    default:
      return type === String(filter).toLowerCase() || text.includes(String(filter).toLowerCase());
  }
};

export default function Certificates() {
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('All Certificates');
  const { data, isFetching, isError, refetch } = useGetCertificatesQuery({ page });
  const [updateCertificate] = useUpdateCertificateMutation();
  const [savingId, setSavingId] = useState(null);

  const certificates = data?.results ?? [];
  const total = data?.count ?? 0;
  const pageSize = data?.page_size ?? 10;
  const start = (page - 1) * pageSize;

  const [preview, setPreview] = useState(null);
  const [shareId, setShareId] = useState(null);

  const handleFilterChange = (nextFilter) => {
    setFilter(nextFilter);
    setPage(1);
  };

  const filteredCertificates = useMemo(() => {
    if (!certificates || certificates.length === 0) return [];
    if (filter === 'All Certificates') return certificates;
    return certificates.filter((item) => matchesFilter(item, filter));
  }, [certificates, filter]);

  const isAll = filter === 'All Certificates';
  const displayTotal = isAll ? total : filteredCertificates.length;
  const listTitle = isAll ? t('All certificates') : t(filter);

  // Both dialogs hold an id rather than a row: the list refetches under them
  // when sharing is switched, and a captured row would show the old state.
  const rowById = (id) => certificates.find((row) => row.id === id) ?? null;
  const previewItem = preview ? rowById(preview.id) ?? preview : null;
  const shareItem = shareId != null ? rowById(shareId) : null;

  /** "Latest" is only the first row of the first page. */
  const latest = page === 1 ? certificates[0] ?? null : null;

  useEffect(() => {
    if (!preview) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setPreview(null);
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [preview]);

  const setPublic = async (item, next) => {
    const body = new FormData();
    body.append('public', next ? 'True' : 'False');

    setSavingId(item.id);
    try {
      await updateCertificate({ id: item.id, body }).unwrap();
    } catch (err) {
      toast.error(
        next ? t('Could not generate the link') : t('Could not disable the link'),
        authErrorMessage(err)
      );
    } finally {
      setSavingId(null);
    }
  };

  const openPreview = (item) => {
    setPreview(item);
  };

  // After the hooks, so none of them are skipped: the hero, the count and the
  // grid all read off a list that isn't there yet, and every one of them has a
  // shape in the skeleton instead.
  if (isFetching && !shareItem && !previewItem) return <CertificatesSkeleton />;

  return (
    <div className="certificates">
      {/* Nothing to headline until the list has loaded, so the hero waits. */}
      {latest && (
        <section className="cert-hero">
          <div className="cert-hero-info">
            <span className="cert-hero-badge">{t('Latest certificate')}</span>
            <h1 className="cert-hero-title">{t(latest.title)}</h1>
            <p className="cert-hero-text">
              {total === 1
                ? t('{{count}} certificate earned, most recently on {{date}}.', { count: total, date: latest.created_at })
                : t('{{count}} certificates earned, most recently on {{date}}.', { count: total, date: latest.created_at })}
            </p>

            <div className="cert-hero-by">
              <span className="cert-hero-avatar">{latest.name?.charAt(0)}</span>
              <span className="cert-hero-name">{latest.name}</span>
              <span className="cert-hero-dot">·</span>
              <span className="cert-hero-ref">{latest.plan_type}</span>
            </div>
          </div>

          <div
            className="cert-hero-preview"
            onClick={() => openPreview(latest)}
            style={{ cursor: 'pointer' }}
          >
            <CertificateArt item={latest} />

            <div className="cert-hero-overlay">
              <Download item={latest} link={latest.link} className="cert-hero-download" />
              {/* logo goes here */}
              <span className="cert-hero-logo" />
            </div>
          </div>
        </section>
      )}

      <section className="cert-list">
        <div className="cert-list-head">
          <div className="cert-list-head-left">
            <AwardIcon />
            <span className="cert-list-title">
              {listTitle} ({displayTotal})
            </span>
          </div>
          <FilterDropdown
            options={FILTER_OPTIONS}
            value={filter}
            onChange={handleFilterChange}
          />
        </div>

        <span className="cert-list-divider" />

        {filteredCertificates.length ? (
          <div className="cert-grid">
            {filteredCertificates.map((item) => (
              <div
                className="cert-item"
                key={item.id}
                onClick={() => openPreview(item)}
                style={{ cursor: 'pointer' }}
              >
                <div className="cert-thumb">
                  <CertificateArt item={item} loading="lazy" />

                  <button
                    type="button"
                    className="cert-expand"
                    onClick={(e) => {
                      e.stopPropagation();
                      openPreview(item);
                    }}
                  >
                    <ExpandIcon />
                  </button>

                  <ShareButton
                    className="cert-public"
                    item={item}
                    onOpen={setShareId}
                  />
                </div>

                <div className="cert-item-meta">
                  <span className="cert-item-id">{item.plan_type}</span>
                  <span className="cert-item-date">{item.created_at}</span>
                </div>

                <Download item={item} link={item.link} className="cert-download" />
              </div>
            ))}
          </div>
        ) : (
          <div className="cert-state">
            <span>
              {isError
                ? t('We could not load your certificates.')
                : certificates.length > 0
                  ? t('No certificates found for "{{filter}}".', { filter: t(filter) })
                  : t('You have not earned any certificates yet.')}
            </span>
            {isError ? (
              <button type="button" className="cert-state__retry" onClick={refetch}>
                {t('Try again')}
              </button>
            ) : certificates.length > 0 ? (
              <button
                type="button"
                className="cert-state__retry"
                onClick={() => handleFilterChange('All Certificates')}
              >
                {t('Clear filter')}
              </button>
            ) : null}
          </div>
        )}

        <Pagination
          page={page}
          pageCount={Math.max(Math.ceil(displayTotal / pageSize), 1)}
          from={displayTotal === 0 ? 0 : start + 1}
          to={Math.min(start + pageSize, displayTotal)}
          total={displayTotal}
          onChange={setPage}
        />

        <PayoutReviewModal
          open={Boolean(previewItem)}
          item={previewItem}
          onClose={() => setPreview(null)}
          onShare={(item) => setShareId(item?.id)}
        />

        <ShareCertificateDialog
          open={shareItem != null}
          item={shareItem}
          busy={savingId === shareId}
          onSetPublic={setPublic}
          onClose={() => setShareId(null)}
        />
      </section>
    </div>
  );
}

/** Some rows arrive with `link: null` — there is nothing to hand over, so the
 *  button is left out rather than pointing at an empty href. */
function Download({ item, link, className }) {
  const targetItem = item || (link ? { link } : null);
  if (!targetItem && !link) return null;

  return (
    <button
      type="button"
      className={className}
      onClick={(e) => {
        e.stopPropagation();
        downloadRenderedCertificate(targetItem);
      }}
    >
      {t('Download')}
      <DownloadIcon />
    </button>
  );
}

/** Opens the share dialog, and carries the row's current state so a shared
 *  certificate is legible from the grid without opening anything. */
function ShareButton({ item, onOpen, className }) {
  const isPublic = Boolean(item.public);

  return (
    <button
      type="button"
      className={isPublic ? `${className} is-public` : className}
      onClick={(e) => {
        e.stopPropagation();
        onOpen(item.id);
      }}
      title={isPublic ? t('Shared — anyone with the link can view it') : t('Share this certificate')}
    >
      <GlobeIcon />
      {isPublic ? t('Shared') : t('Share')}
    </button>
  );
}

function GlobeIcon() {
  return (
    <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.1">
      <circle cx="6" cy="6" r="4.2" />
      <path d="M1.8 6h8.4" />
      <path d="M6 1.8c1.1 1.2 1.7 2.6 1.7 4.2S7.1 8.9 6 10.2C4.9 8.9 4.3 7.5 4.3 6s.6-3 1.7-4.2Z" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.1">
      <path d="M6 1.8v6" />
      <path d="M3.6 5.6 6 8l2.4-2.4" />
      <path d="M2.2 10.2h7.6" />
    </svg>
  );
}

function ExpandIcon() {
  return (
    <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.1">
      <path d="M7.2 1.8h3v3" />
      <path d="M4.8 10.2h-3v-3" />
      <path d="M10.2 1.8 6.9 5.1" />
      <path d="M1.8 10.2l3.3-3.3" />
    </svg>
  );
}

function AwardIcon() {
  return (
    <svg className="cert-list-icon" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.1">
      <circle cx="7" cy="5.2" r="3.4" />
      <path d="M5 8.4 4.2 12.4l2.8-1.5 2.8 1.5L9 8.4" />
    </svg>
  );
}
